import axios from "axios";

const baseUrl = process.env.REACT_APP_GITO_API_URL + "/charging-sessions/ChargingSessions";
//const baseUrl = "https://localhost:7241/ChargingSessions";


export async function getSampledValuesForConnector(apiAccessToken, chargingConnectorPublicId) {
    try {
        const response = await axios.get(
            `${baseUrl}/get-sampled-values-for-connector/${chargingConnectorPublicId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch charging connector sampledValues"
        );
    }
}

export async function getSampledValuesForSession(apiAccessToken, chargingSessionPublicID) {
    try {
        const response = await axios.get(
            `${baseUrl}/get-sampled-values-for-sessionPublicId/${chargingSessionPublicID}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch charging session sampledValues"
        );
    }
}

export async function getSessionDetails(apiAccessToken, chargingSessionPublicId) {
    try {
        const response = await axios.get(
            `${baseUrl}/get-session-details/${chargingSessionPublicId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch charging session details"
        );
    }
}

export async function getSessionReceiptPdf(apiAccessToken, chargingSessionPublicId) {
    try {
        const response = await axios.get(
            `${baseUrl}/get-session-receipt-pdf/${chargingSessionPublicId}`,
            {
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        let detail = "Failed to fetch session receipt";
        const data = error.response?.data;

        if (data && typeof data.text === "function") {
            try {
                const text = await data.text();
                const parsed = JSON.parse(text);
                detail = parsed?.detail || detail;
            } catch {
                detail = "Failed to fetch session receipt";
            }
        } else if (data?.detail) {
            detail = data.detail;
        }

        throw new Error(detail);
    }
}

export async function emailSessionReceipt(apiAccessToken, chargingSessionPublicId, email) {
    try {
        const response = await axios.post(
            `${baseUrl}/email-session-receipt/${chargingSessionPublicId}`,
            { email },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to email session receipt"
        );
    }
}

export async function createCreditCdr(apiAccessToken, cdrPublicId) {
    try {
        const response = await axios.post(
            `${baseUrl}/credit-cdr/${encodeURIComponent(cdrPublicId)}`,
            null,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to create credit CDR"
        );
    }
}

export async function getSessionDetailsByConnector(apiAccessToken, connectorPublicId) {
    try {
        const response = await axios.get(
            `${baseUrl}/get-session-details-for-connector/${connectorPublicId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch charging connector session details"
        );
    }
}

export async function getSessionDetailsByConnectors(apiAccessToken, connectorPublicIds) {
    const ids = Array.isArray(connectorPublicIds)
        ? connectorPublicIds.filter((id) => typeof id === "string" && id.trim() !== "")
        : [];

    if (ids.length === 0) {
        return [];
    }

    try {
        const response = await axios.post(
            `${baseUrl}/get-session-details-for-connectors`,
            { chargingConnectorPublicIds: ids },
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch charging connector session details"
        );
    }
}

export async function getSessionStatisticsSummary(apiAccessToken, filter) {
    try {
        const response = await axios.post(
            `${baseUrl}/session-statistics-summary`,
            filter,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch session statistics summary"
        );
    }
}

export async function getSessionStatisticsChart(apiAccessToken, filter) {
    try {
        const response = await axios.post(
            `${baseUrl}/session-statistics-chart`,
            filter,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch session statistics chart"
        );
    }
}


export async function getSessionStatisticsList(apiAccessToken, filter) {
    try {
        const response = await axios.post(
            `${baseUrl}/session-statistics-list`,
            filter,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch session statistics list"
        );
    }
}


export async function exportSessionStatistics(apiAccessToken, filter) {
    try {
        const response = await axios.post(
            `${baseUrl}/session-statistics-export`,
            filter,
            {
                responseType: "blob",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiAccessToken}`,
                },
            }
        );

        return response.data; // Blob
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to export session statistics"
        );
    }
}

export async function getCustomerStatistics(apiAccessToken, filter) {
    try {
        const response = await axios.post(
            `${baseUrl}/customer-statistics`,
            filter,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to fetch customer statistics"
        );
    }
}

export async function exportCustomerStatisticsPdf(apiAccessToken, filter) {
    try {
        const response = await axios.post(
            `${baseUrl}/customer-statistics-pdf`,
            filter,
            {
                responseType: "blob",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.detail || "Failed to export customer statistics"
        );
    }
}
